import { getProductBrand } from "@lib/util/product-specs"
import { HttpTypes } from "@medusajs/types"

export const USED_LISTING_ZONE = "used"
export const USED_COLLECTION_HANDLE = "used"

export type UsedFilterKey = "brand" | "category" | "condition" | "availability"

export type UsedFilters = {
  brand?: string
  category?: string
  condition?: string
  /** Empty or "available" hides sold items. "all" includes them. */
  availability?: string
  q?: string
}

export type UsedFilterChoice = {
  value: string
  label: string
}

export type UsedFilterOptions = {
  brand: string[]
  category: UsedFilterChoice[]
  condition: UsedFilterChoice[]
}

const CATEGORIES: UsedFilterChoice[] = [
  { value: "ap", label: "AP" },
  { value: "switch", label: "Switch" },
  { value: "router", label: "Router" },
  { value: "camera", label: "กล้อง" },
  { value: "accessory", label: "อุปกรณ์เสริม" },
  { value: "other", label: "อื่นๆ" },
]

const CONDITIONS: UsedFilterChoice[] = [
  { value: "like-new", label: "เหมือนใหม่" },
  { value: "good", label: "สภาพดี" },
  { value: "worn", label: "มีรอยใช้งาน" },
]

function metadataOf(product: { metadata?: Record<string, unknown> | null }) {
  return (product.metadata || {}) as Record<string, unknown>
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

export function isUsedListing(product: {
  metadata?: Record<string, unknown> | null
}): boolean {
  return text(metadataOf(product).listing_zone) === USED_LISTING_ZONE
}

export function getUsedCategoryKey(product: HttpTypes.StoreProduct): string {
  return text(metadataOf(product).category)
    .toLowerCase()
    .replace(/_/g, "-")
}

export function getUsedCategoryLabel(key: string): string {
  const normalized = key.trim().toLowerCase().replace(/_/g, "-")
  return (
    CATEGORIES.find((item) => item.value === normalized)?.label ||
    normalized.replace(/-/g, " ")
  )
}

export function getUsedConditionKey(product: HttpTypes.StoreProduct): string {
  return text(metadataOf(product).condition)
    .toLowerCase()
    .replace(/_/g, "-")
}

export function getUsedConditionLabel(key: string): string {
  const normalized = key.trim().toLowerCase().replace(/_/g, "-")
  return CONDITIONS.find((item) => item.value === normalized)?.label || normalized
}

export function getUsedConditionNote(product: HttpTypes.StoreProduct): string {
  return text(metadataOf(product).condition_note)
}

export function getUsedSpecs(product: HttpTypes.StoreProduct): string[] {
  const raw = metadataOf(product).used_specs
  if (Array.isArray(raw)) {
    return raw.map((item) => text(item)).filter(Boolean)
  }

  const value = text(raw)
  if (!value) return []

  try {
    const parsed = JSON.parse(value) as unknown
    if (Array.isArray(parsed)) {
      return parsed.map((item) => text(item)).filter(Boolean)
    }
  } catch {
    // fall through to line/comma split
  }

  return value
    .split(/\n|,/)
    .map((part) => part.trim())
    .filter(Boolean)
}

export function getListingStock(product: HttpTypes.StoreProduct): number | null {
  const managed = (product.variants || []).filter(
    (variant) => variant.manage_inventory && !variant.allow_backorder
  )
  if (!managed.length) return null
  return managed.reduce(
    (sum, variant) => sum + (variant.inventory_quantity || 0),
    0
  )
}

export function isUsedSold(product: HttpTypes.StoreProduct): boolean {
  if (!isUsedListing(product)) return false
  const stock = getListingStock(product)
  return stock !== null && stock <= 0
}

export function productMatchesUsedFilters(
  product: HttpTypes.StoreProduct,
  filters: UsedFilters
): boolean {
  if (!isUsedListing(product)) return false

  if (filters.brand && getProductBrand(product) !== filters.brand) return false
  if (filters.category && getUsedCategoryKey(product) !== filters.category) {
    return false
  }
  if (filters.condition && getUsedConditionKey(product) !== filters.condition) {
    return false
  }

  const availability = filters.availability || "available"
  if (availability !== "all" && isUsedSold(product)) return false

  const query = filters.q?.trim().toLowerCase()
  if (query) {
    const haystack = [
      product.title,
      product.handle,
      getProductBrand(product),
      getUsedCategoryLabel(getUsedCategoryKey(product)),
      getUsedConditionLabel(getUsedConditionKey(product)),
      getUsedConditionNote(product),
      ...getUsedSpecs(product),
    ]
      .join(" ")
      .toLowerCase()
    const tokens = query.split(/\s+/).filter(Boolean)
    if (!tokens.every((token) => haystack.includes(token))) return false
  }

  return true
}

export function getUsedFilterOptions(
  products: HttpTypes.StoreProduct[]
): UsedFilterOptions {
  const brands = new Set<string>()
  const categoryKeys = new Set<string>()
  const conditionKeys = new Set<string>()

  for (const product of products) {
    const brand = getProductBrand(product)
    if (brand) brands.add(brand)
    const category = getUsedCategoryKey(product)
    if (category) categoryKeys.add(category)
    const condition = getUsedConditionKey(product)
    if (condition) conditionKeys.add(condition)
  }

  const categoryOrder = new Map(CATEGORIES.map((item, index) => [item.value, index]))
  const conditionOrder = new Map(
    CONDITIONS.map((item, index) => [item.value, index])
  )

  return {
    brand: [...brands].sort((a, b) => a.localeCompare(b, "en")),
    category: [...categoryKeys]
      .sort(
        (a, b) =>
          (categoryOrder.get(a) ?? 99) - (categoryOrder.get(b) ?? 99) ||
          a.localeCompare(b)
      )
      .map((value) => ({ value, label: getUsedCategoryLabel(value) })),
    condition: [...conditionKeys]
      .sort(
        (a, b) =>
          (conditionOrder.get(a) ?? 99) - (conditionOrder.get(b) ?? 99) ||
          a.localeCompare(b)
      )
      .map((value) => ({ value, label: getUsedConditionLabel(value) })),
  }
}
